import { useEffect, useState } from "react";
import { Layers, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminAdjustStoreBvMutation, useStoreBvBalance } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

interface AdminAdjustStoreBvDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: number;
  storeName: string;
}

export function AdminAdjustStoreBvDialog({
  open,
  onOpenChange,
  storeId,
  storeName,
}: AdminAdjustStoreBvDialogProps) {
  const { toast } = useToast();
  const adjustMutation = useAdminAdjustStoreBvMutation(storeId);
  const { data: balance, isLoading } = useStoreBvBalance(open ? storeId : undefined);

  const [direction, setDirection] = useState<"credit" | "debit">("credit");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) {
      setDirection("credit");
      setAmount("");
      setReason("");
    }
  }, [open]);

  const handleSubmit = async () => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast({ title: "Enter a valid BV amount", variant: "destructive" });
      return;
    }
    if (reason.trim().length < 3) {
      toast({ title: "Reason is required", description: "Use at least 3 characters.", variant: "destructive" });
      return;
    }

    try {
      const updated = await adjustMutation.mutateAsync({
        amount: parsed,
        direction,
        reason: reason.trim(),
      });
      toast({
        title: direction === "credit" ? "BV credited" : "BV deducted",
        description: `Available BV: ${updated.availableBv}`,
      });
      onOpenChange(false);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" /> Adjust store BV
          </DialogTitle>
          <DialogDescription>
            Credit or debit Business Volume for <strong>{storeName}</strong>. Store uses this BV on products.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
            {isLoading ? (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading balance…
              </span>
            ) : (
              <>
                Available BV: <strong>{balance?.availableBv ?? 0}</strong>
                <span className="text-muted-foreground">
                  {" "}
                  · Received {balance?.totalBvReceived ?? 0} · Allocated {balance?.totalBvAllocated ?? 0}
                </span>
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label>Direction</Label>
            <Select value={direction} onValueChange={(v) => setDirection(v as "credit" | "debit")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">Credit (add BV)</SelectItem>
                <SelectItem value="debit">Debit (remove BV)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bv-amount">Amount</Label>
            <Input
              id="bv-amount"
              type="number"
              min={0.01}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bv-reason">Reason</Label>
            <Textarea
              id="bv-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this BV being adjusted?"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={adjustMutation.isPending}>
            {adjustMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
